import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Checkbox, Input, Modal, Select } from '../../../shared/components'
import { fetchAddressByCEP, formatCEP } from '../../../services/viacep'
import { useId } from 'react'

export interface AddressFormValues {
  id?: string
  name: string
  street: string
  number: string
  complement?: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  isDefault: boolean
}

interface AddressModalFormProps {
  isOpen: boolean
  title: string
  values: AddressFormValues
  onClose: () => void
  onSubmit: (data: AddressFormValues) => void
  isSubmitting: boolean
}

const AddressModalForm: React.FC<AddressModalFormProps> = ({
  isOpen,
  title,
  values,
  onClose,
  onSubmit,
  isSubmitting
}) => {
  const formId = useId()
  const [isLoadingCEP, setIsLoadingCEP] = useState(false)
  const [cepError, setCepError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm<AddressFormValues>({
    defaultValues: values
  })

  useEffect(() => {
    if (isOpen) {
      reset(values)
      setCepError(null)
    }
  }, [isOpen, values, reset])

  const handleFormSubmit = (data: AddressFormValues) => {
    onSubmit(data)
  }

  const handleCEPBlur = async (cep: string) => {
    const cleanCEP = cep.replace(/\D/g, '')
    
    if (cleanCEP.length !== 8) {
      return
    }

    setIsLoadingCEP(true)
    setCepError(null)

    try {
      const addressData = await fetchAddressByCEP(cleanCEP)

      if (addressData) {
        setValue('zipCode', formatCEP(addressData.zipCode))
        setValue('street', addressData.street)
        setValue('neighborhood', addressData.neighborhood)
        setValue('city', addressData.city)
        setValue('state', addressData.state)
      }
    } catch (error) {
      setCepError(error instanceof Error ? error.message : 'Erro ao consultar CEP')
    } finally {
      setIsLoadingCEP(false)
    }
  }

  const formatZipCode = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '')
    let formatted = cleanValue
    
    if (cleanValue.length > 8) {
      formatted = cleanValue.slice(0, 8)
    }
    
    if (formatted.length > 5) {
      formatted = formatted.replace(/(\d{5})(\d{0,3})/, '$1-$2')
    }
    
    return formatted
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      overlayClassName="bg-black bg-opacity-50"
      contentClassName="w-full max-w-2xl"
      bodyStyle={{ padding: 0 }}
      footer={
        <div className="flex w-full justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Endereço *
              </label>
              <Input
                type="text"
                {...register('name', {
                  required: 'Nome do endereço é obrigatório'
                })}
                placeholder="Ex: Casa, Trabalho..."
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CEP *
                {isLoadingCEP && (
                  <span className="ml-2 text-xs text-blue-600">Consultando...</span>
                )}
              </label>
              <div className="relative">
                <Controller
                  name="zipCode"
                  control={control}
                  rules={{
                    required: 'CEP é obrigatório',
                    pattern: {
                      value: /^\d{5}-?\d{3}$/,
                      message: 'CEP inválido'
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      type="text"
                      value={field.value || ''}
                      onChange={(e) => {
                        const formatted = formatZipCode(e.target.value)
                        field.onChange(formatted)
                        setCepError(null)
                      }}
                      onBlur={(e) => {
                        field.onBlur()
                        handleCEPBlur(e.target.value)
                      }}
                      placeholder="00000-000"
                      maxLength={9}
                      error={!!(errors.zipCode || cepError)}
                      helperText={errors.zipCode?.message || cepError || undefined}
                    />
                  )}
                />
                {isLoadingCEP && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rua *
              </label>
              <Input
                type="text"
                {...register('street', {
                  required: 'Rua é obrigatória'
                })}
                error={!!errors.street}
                helperText={errors.street?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número *
              </label>
              <Input
                type="text"
                {...register('number', {
                  required: 'Número é obrigatório'
                })}
                error={!!errors.number}
                helperText={errors.number?.message}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Complemento
            </label>
            <Input
              type="text"
              {...register('complement')}
              placeholder="Apto, Bloco, etc..."
              error={!!errors.complement}
              helperText={errors.complement?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bairro *
            </label>
            <Input
              type="text"
              {...register('neighborhood', {
                required: 'Bairro é obrigatório'
              })}
              error={!!errors.neighborhood}
              helperText={errors.neighborhood?.message}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cidade *
              </label>
              <Input
                type="text"
                {...register('city', {
                  required: 'Cidade é obrigatória'
                })}
                error={!!errors.city}
                helperText={errors.city?.message}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado *
              </label>
              <Controller
                name="state"
                control={control}
                rules={{ required: 'Estado é obrigatório' }}
                render={({ field }) => (
                  <>
                    <Select
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      options={[
                        { value: '', label: 'UF' },
                        ...['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => ({
                          value: uf,
                          label: uf
                        }))
                      ]}
                      placeholder="UF"
                      className="w-full"
                    />
                    {errors.state && (
                      <p className="mt-1 text-xs text-red-600" role="alert">
                        {errors.state.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Controller
              name="isDefault"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="address-default"
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
            <label htmlFor="address-default" className="text-sm font-medium text-gray-700">
              Definir como endereço padrão
            </label>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default AddressModalForm
